/**
 * I-589 Asylum Application DOCX Worksheet Generator
 *
 * Generates a companion DOCX worksheet with all collected I-589 data
 * organized by section — useful for attorney review before filing.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Footer,
  PageNumber,
  AlignmentType,
  convertInchesToTwip,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from 'docx';
import type { I589FormData } from './i589FormFiller';

// ============================================================================
// Helpers
// ============================================================================

const DISCLAIMER_EN =
  'ATTORNEY-CLIENT PRIVILEGED — FOR REVIEW PURPOSES ONLY. Not a final USCIS submission.';
const DISCLAIMER_ES =
  'PRIVILEGIO ABOGADO-CLIENTE — SOLO PARA FINES DE REVISIÓN. No es una presentación final ante USCIS.';

// Spanish label translations
const ES_LABELS: Record<string, string> = {
  // Headings
  'I-589 Asylum Application': 'Solicitud de Asilo I-589',
  'Applicant:': 'Solicitante:',
  'Prepared:': 'Preparado:',
  'PART A.I — APPLICANT INFORMATION': 'PARTE A.I — INFORMACIÓN DEL SOLICITANTE',
  'Identity': 'Identidad',
  'Immigration Status': 'Estado Migratorio',
  'Current Address': 'Dirección Actual',
  'PART A.II — SPOUSE INFORMATION': 'PARTE A.II — INFORMACIÓN DEL CÓNYUGE',
  'PART A.II — CHILDREN': 'PARTE A.II — HIJOS',
  'PART A.III — BACKGROUND INFORMATION': 'PARTE A.III — INFORMACIÓN DE ANTECEDENTES',
  'PART B — PERSECUTION CLAIM': 'PARTE B — RECLAMO DE PERSECUCIÓN',
  'PART C — ADDITIONAL CLAIMS': 'PARTE C — RECLAMOS ADICIONALES',
  'PART E — ATTORNEY / PREPARER INFORMATION': 'PARTE E — INFORMACIÓN DEL ABOGADO / PREPARADOR',
  // Field labels
  'Last Name': 'Apellido',
  'First Name': 'Nombre',
  'Middle Name': 'Segundo Nombre',
  'Other Names / Aliases': 'Otros Nombres / Alias',
  'Date of Birth': 'Fecha de Nacimiento',
  'City of Birth': 'Ciudad de Nacimiento',
  'Country of Birth': 'País de Nacimiento',
  'Nationality / Citizenship': 'Nacionalidad / Ciudadanía',
  'Nationality': 'Nacionalidad',
  'Race / Ethnic Group': 'Raza / Grupo Étnico',
  'Religion': 'Religión',
  'Gender': 'Género',
  'Marital Status': 'Estado Civil',
  'A-Number': 'Número A',
  'Social Security Number': 'Número de Seguro Social',
  'I-94 Number': 'Número I-94',
  'Date of Last Entry to US': 'Fecha de Última Entrada a EE.UU.',
  'Place of Last Entry': 'Lugar de Última Entrada',
  'Immigration Status at Entry': 'Estado Migratorio al Entrar',
  'Authorized Stay Expiry': 'Vencimiento de Estadía Autorizada',
  'Street Address': 'Dirección',
  'City': 'Ciudad',
  'State': 'Estado',
  'ZIP Code': 'Código Postal',
  'Phone': 'Teléfono',
  'Currently in US': 'Actualmente en EE.UU.',
  'Total Number of Children': 'Número Total de Hijos',
  'Last Country Before US': 'Último País Antes de EE.UU.',
  'Previous Asylum Applications': 'Solicitudes de Asilo Anteriores',
  'Persecution Grounds': 'Motivos de Persecución',
  'Past Persecution': 'Persecución Pasada',
  'Fears Return': 'Teme Regresar',
  'Withholding of Removal (Other Country)': 'Suspensión de Deportación (Otro País)',
  'Currently in Immigration Proceedings': 'Actualmente en Procedimientos Migratorios',
  'Attorney Name': 'Nombre del Abogado',
  'State Bar Number': 'Número de Colegio de Abogados',
  // Narrative labels
  'PERSECUTION NARRATIVE (Part B Statement):': 'NARRATIVA DE PERSECUCIÓN (Declaración Parte B):',
  'Countries Traveled Through to US:': 'Países por los que Viajó hacia EE.UU.:',
  'Previous Application Details:': 'Detalles de Solicitudes Anteriores:',
  'Education History:': 'Historial Educativo:',
  'Employment History (Last 5 Years):': 'Historial Laboral (Últimos 5 Años):',
  'Other Country Claim Details:': 'Detalles del Reclamo de Otro País:',
  // Misc
  'Yes': 'Sí',
  'No': 'No',
  '(No spouse information provided)': '(No se proporcionó información del cónyuge)',
  '(not provided)': '(no proporcionado)',
  'Child': 'Hijo/a',
};

/** Get label in the requested language */
function L(label: string, language: 'en' | 'es'): string {
  if (language === 'es' && ES_LABELS[label]) return ES_LABELS[label];
  return label;
}

/** Translate common field values when language=es */
const ES_VALUES: Record<string, string> = {
  'Female': 'Femenino',
  'female': 'Femenino',
  'Male': 'Masculino',
  'male': 'Masculino',
  'Single': 'Soltero/a',
  'single': 'Soltero/a',
  'Married': 'Casado/a',
  'married': 'Casado/a',
  'Divorced': 'Divorciado/a',
  'divorced': 'Divorciado/a',
  'Widowed': 'Viudo/a',
  'widowed': 'Viudo/a',
};

function V(value: string | undefined, language: 'en' | 'es'): string | undefined {
  if (!value || language !== 'es') return value;
  return ES_VALUES[value] || value;
}

function heading1(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 360, after: 120 },
    children: [
      new TextRun({
        text,
        bold: true,
        size: 26,
        font: 'Arial',
        color: '1e3a5f',
      }),
    ],
  });
}

function heading2(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 200, after: 80 },
    children: [
      new TextRun({
        text,
        bold: true,
        size: 22,
        font: 'Arial',
      }),
    ],
  });
}

function fieldRow(label: string, value: string | undefined): TableRow {
  return new TableRow({
    children: [
      new TableCell({
        width: { size: 35, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          left: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          right: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
        },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: label,
                bold: true,
                size: 18,
                font: 'Arial',
                color: '444444',
              }),
            ],
            spacing: { before: 60, after: 60 },
          }),
        ],
      }),
      new TableCell({
        width: { size: 65, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          left: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
          right: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
        },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: value || '—',
                size: 18,
                font: 'Arial',
                color: value ? '111111' : '999999',
                italics: !value,
              }),
            ],
            spacing: { before: 60, after: 60 },
          }),
        ],
      }),
    ],
  });
}

function sectionTable(rows: TableRow[]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows,
  });
}

function narrativeBlock(label: string, value: string | undefined, language: 'en' | 'es' = 'en'): Paragraph[] {
  const fallback = language === 'es' ? '(no proporcionado)' : '(not provided)';
  return [
    new Paragraph({
      spacing: { before: 160, after: 60 },
      children: [
        new TextRun({
          text: label,
          bold: true,
          size: 20,
          font: 'Arial',
          color: '1e3a5f',
        }),
      ],
    }),
    new Paragraph({
      spacing: { before: 60, after: 200 },
      border: {
        left: { style: BorderStyle.SINGLE, size: 6, color: '3b6fa0' },
      },
      indent: { left: convertInchesToTwip(0.25) },
      children: [
        new TextRun({
          text: value || fallback,
          size: 18,
          font: 'Arial',
          color: value ? '111111' : '999999',
          italics: !value,
        }),
      ],
    }),
  ];
}

function spacer(): Paragraph {
  return new Paragraph({ spacing: { before: 80, after: 80 }, children: [] });
}

// ============================================================================
// Worksheet Builder
// ============================================================================

export interface BuildI589WorksheetParams {
  data: I589FormData;
  createdAt?: Date;
  applicantFullName?: string;
  language?: 'en' | 'es';
}

export async function buildI589DocxWorksheet(params: BuildI589WorksheetParams): Promise<File> {
  const { data } = params;
  const language = params.language || 'en';
  const DISCLAIMER_TEXT = language === 'es' ? DISCLAIMER_ES : DISCLAIMER_EN;
  const createdAt = params.createdAt || new Date();
  const dateStr = createdAt.toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const applicantName =
    params.applicantFullName ||
    [data.first_name, data.middle_name, data.last_name].filter(Boolean).join(' ') ||
    'Unknown Applicant';

  const titleText = L('I-589 Asylum Application', language);
  const displayTitle = `${titleText} — ${applicantName}`;

  // -------------------------------------------------------------------------
  // Content paragraphs
  // -------------------------------------------------------------------------
  const children: Array<Paragraph | Table> = [];

  // Title
  children.push(
    new Paragraph({
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 160 },
      children: [
        new TextRun({
          text: titleText,
          bold: true,
          size: 40,
          font: 'Arial',
          color: '1e3a5f',
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 80 },
      children: [
        new TextRun({
          text: `${L('Applicant:', language)} ${applicantName}`,
          size: 24,
          font: 'Arial',
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 80 },
      children: [
        new TextRun({
          text: `${L('Prepared:', language)} ${dateStr}`,
          size: 20,
          font: 'Arial',
          color: '666666',
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: DISCLAIMER_TEXT,
          italics: true,
          size: 16,
          font: 'Arial',
          color: 'cc3300',
        }),
      ],
    })
  );

  // ---- Part A.I: Identity ----
  children.push(heading1(L('PART A.I — APPLICANT INFORMATION', language)), spacer());
  children.push(heading2(L('Identity', language)));
  children.push(
    sectionTable([
      fieldRow(L('Last Name', language), data.last_name),
      fieldRow(L('First Name', language), data.first_name),
      fieldRow(L('Middle Name', language), data.middle_name),
      fieldRow(L('Other Names / Aliases', language), data.aliases),
      fieldRow(L('Date of Birth', language), data.dob),
      fieldRow(L('City of Birth', language), data.city_of_birth),
      fieldRow(L('Country of Birth', language), data.country_of_birth),
      fieldRow(L('Nationality / Citizenship', language), data.nationality),
      fieldRow(L('Race / Ethnic Group', language), data.race_ethnicity),
      fieldRow(L('Religion', language), data.religion),
      fieldRow(L('Gender', language), V(data.gender, language)),
      fieldRow(L('Marital Status', language), V(data.marital_status, language)),
    ]),
    spacer()
  );

  children.push(heading2(L('Immigration Status', language)));
  children.push(
    sectionTable([
      fieldRow(L('A-Number', language), data.a_number),
      fieldRow(L('Social Security Number', language), data.ssn),
      fieldRow(L('I-94 Number', language), data.i94_number),
      fieldRow(L('Date of Last Entry to US', language), data.last_entry_date),
      fieldRow(L('Place of Last Entry', language), data.last_entry_place),
      fieldRow(L('Immigration Status at Entry', language), data.immigration_status),
      fieldRow(L('Authorized Stay Expiry', language), data.authorized_stay_expiry),
    ]),
    spacer()
  );

  children.push(heading2(L('Current Address', language)));
  children.push(
    sectionTable([
      fieldRow(L('Street Address', language), data.residence_street),
      fieldRow(L('City', language), data.residence_city),
      fieldRow(L('State', language), data.residence_state),
      fieldRow(L('ZIP Code', language), data.residence_zip),
      fieldRow(L('Phone', language), data.residence_phone),
    ]),
    spacer()
  );

  // ---- Part A.II: Spouse ----
  children.push(heading1(L('PART A.II — SPOUSE INFORMATION', language)), spacer());
  if (data.spouse_last_name) {
    children.push(
      sectionTable([
        fieldRow(L('Last Name', language), data.spouse_last_name),
        fieldRow(L('First Name', language), data.spouse_first_name),
        fieldRow(L('Middle Name', language), data.spouse_middle_name),
        fieldRow(L('Date of Birth', language), data.spouse_dob),
        fieldRow(L('Nationality', language), data.spouse_nationality),
        fieldRow(L('A-Number', language), data.spouse_a_number),
        fieldRow(L('Currently in US', language), data.spouse_in_us === 'true' ? L('Yes', language) : data.spouse_in_us === 'false' ? L('No', language) : data.spouse_in_us),
      ]),
      spacer()
    );
  } else {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: L('(No spouse information provided)', language), italics: true, size: 18, font: 'Arial', color: '999999' })],
        spacing: { before: 80, after: 200 },
      })
    );
  }

  // ---- Part A.II: Children ----
  children.push(heading1(L('PART A.II — CHILDREN', language)), spacer());
  children.push(
    sectionTable([fieldRow(L('Total Number of Children', language), data.num_children)]),
    spacer()
  );

  const childData = [
    { n: 1, last: data.child1_last_name, first: data.child1_first_name, dob: data.child1_dob, nat: data.child1_nationality, inUs: data.child1_in_us },
    { n: 2, last: data.child2_last_name, first: data.child2_first_name, dob: data.child2_dob, nat: data.child2_nationality, inUs: undefined },
    { n: 3, last: data.child3_last_name, first: data.child3_first_name, dob: data.child3_dob, nat: data.child3_nationality, inUs: undefined },
    { n: 4, last: data.child4_last_name, first: data.child4_first_name, dob: data.child4_dob, nat: data.child4_nationality, inUs: undefined },
  ];

  for (const c of childData) {
    if (c.last || c.first) {
      children.push(heading2(`${L('Child', language)} ${c.n}`));
      const rows = [
        fieldRow(L('Last Name', language), c.last),
        fieldRow(L('First Name', language), c.first),
        fieldRow(L('Date of Birth', language), c.dob),
        fieldRow(L('Nationality', language), c.nat),
      ];
      if (c.inUs !== undefined) {
        rows.push(fieldRow(L('Currently in US', language), c.inUs === 'true' ? L('Yes', language) : c.inUs === 'false' ? L('No', language) : c.inUs));
      }
      children.push(sectionTable(rows), spacer());
    }
  }

  // ---- Part A.III: Background ----
  children.push(heading1(L('PART A.III — BACKGROUND INFORMATION', language)), spacer());
  children.push(
    sectionTable([
      fieldRow(L('Last Country Before US', language), data.last_country_before_us),
      fieldRow(L('Previous Asylum Applications', language), data.previous_applications === 'true' ? L('Yes', language) : data.previous_applications === 'false' ? L('No', language) : data.previous_applications),
    ]),
    spacer()
  );

  children.push(...narrativeBlock(L('Countries Traveled Through to US:', language), data.travel_history, language));
  if (data.previous_applications === 'true') {
    children.push(...narrativeBlock(L('Previous Application Details:', language), data.previous_applications_detail, language));
  }
  children.push(...narrativeBlock(L('Education History:', language), data.education_history, language));
  children.push(...narrativeBlock(L('Employment History (Last 5 Years):', language), data.employment_history, language));

  // ---- Part B: Persecution ----
  children.push(heading1(L('PART B — PERSECUTION CLAIM', language)), spacer());
  children.push(
    sectionTable([
      fieldRow(L('Persecution Grounds', language), data.persecution_grounds),
      fieldRow(L('Past Persecution', language), data.persecution_past === 'true' ? L('Yes', language) : data.persecution_past === 'false' ? L('No', language) : data.persecution_past),
      fieldRow(L('Fears Return', language), data.persecution_fear_return === 'true' ? L('Yes', language) : data.persecution_fear_return === 'false' ? L('No', language) : data.persecution_fear_return),
    ]),
    spacer()
  );

  children.push(...narrativeBlock(L('PERSECUTION NARRATIVE (Part B Statement):', language), data.persecution_narrative, language));

  // ---- Part C: Additional ----
  children.push(heading1(L('PART C — ADDITIONAL CLAIMS', language)), spacer());
  children.push(
    sectionTable([
      fieldRow(L('Withholding of Removal (Other Country)', language), data.other_country_claim === 'true' ? L('Yes', language) : data.other_country_claim === 'false' ? L('No', language) : data.other_country_claim),
      fieldRow(L('Currently in Immigration Proceedings', language), data.immigration_proceedings === 'true' ? L('Yes', language) : data.immigration_proceedings === 'false' ? L('No', language) : data.immigration_proceedings),
    ]),
    spacer()
  );
  if (data.other_country_detail) {
    children.push(...narrativeBlock(L('Other Country Claim Details:', language), data.other_country_detail, language));
  }

  // ---- Part E: Attorney ----
  children.push(heading1(L('PART E — ATTORNEY / PREPARER INFORMATION', language)), spacer());
  children.push(
    sectionTable([
      fieldRow(L('Attorney Name', language), data.attorney_name),
      fieldRow(L('State Bar Number', language), data.attorney_bar_number),
      fieldRow(L('Street Address', language), data.attorney_street),
      fieldRow(L('City', language), data.attorney_city),
      fieldRow(L('State', language), data.attorney_state),
      fieldRow(L('ZIP Code', language), data.attorney_zip),
    ]),
    spacer()
  );

  // -------------------------------------------------------------------------
  // Footer
  // -------------------------------------------------------------------------
  const contentFooter = new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            children: ['Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES],
            size: 16,
            font: 'Arial',
            color: '666666',
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: DISCLAIMER_TEXT,
            italics: true,
            size: 14,
            font: 'Arial',
            color: 'cc3300',
          }),
        ],
      }),
    ],
  });

  // -------------------------------------------------------------------------
  // Assemble document
  // -------------------------------------------------------------------------
  const doc = new Document({
    title: displayTitle,
    creator: 'Legal Document Creator — I-589 Asylum Module',
    description: 'I-589 Asylum Application Worksheet',
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
        },
        footers: { default: contentFooter },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const datePart = createdAt.toISOString().slice(0, 10);
  const filenamePart = applicantName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const filename = `i-589-worksheet-${filenamePart}-${datePart}.docx`;

  return new File([blob], filename, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}
